/**
 * CountryFeatureBadge — zeigt das Land eines Mandanten + einen Stub-Hinweis
 * für Länder mit eingeschränktem Funktionsumfang (CH, Stand Feb 2026).
 *
 * Sprint 0 / Weg-3-Vorbereitung.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modes:
 *   - 'self'    → liest /api/me/country-config (FL-Eigenkonto)
 *   - 'client'  → liest /api/advisor/clients/{clientId}/country-config (SB-Sicht)
 *   - direct    → wenn `config` als Prop reingegeben wird, kein Fetch
 *
 * Variants:
 *   - 'badge'  (default): kompakte Pill mit Flagge + Code, Info-Icon bei Stub
 *   - 'banner': vollbreite Banner-Box mit kompletten Hinweis-Text (nur wenn Stub)
 */
export function CountryFeatureBadge({
  mode = 'self',
  clientId,
  config,
  variant = 'badge',
  authHeader = null,
  className = '',
}) {
  const [data, setData] = useState(config || null);

  useEffect(() => {
    if (config) {
      setData(config);
      return;
    }
    if (mode === 'self') {
      const t = localStorage.getItem('access_token') || localStorage.getItem('token');
      axios
        .get(`${API}/me/country-config`, { headers: { Authorization: `Bearer ${t}` } })
        .then((r) => setData(r.data))
        .catch(() => setData(null));
    } else if (mode === 'client' && clientId) {
      const t = authHeader || (localStorage.getItem('advisor_token') ? `Bearer ${localStorage.getItem('advisor_token')}` : null);
      if (!t) return;
      axios
        .get(`${API}/advisor/clients/${clientId}/country-config`, { headers: { Authorization: t } })
        .then((r) => setData(r.data))
        .catch(() => setData(null));
    }
  }, [mode, clientId, config, authHeader]);

  if (!data) return null;
  const incomplete = !data.features_complete;

  if (variant === 'banner') {
    if (!incomplete) return null;
    return (
      <div
        className={`flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 ${className}`}
        data-testid="country-feature-banner"
      >
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
        <div className="text-sm text-amber-900">
          <span className="mr-1.5 text-base align-text-bottom">{data.flag}</span>
          <strong>{data.name}</strong> — {data.feature_notes || 'Eingeschränkter Funktionsumfang.'}
        </div>
      </div>
    );
  }

  // Badge
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${
        incomplete ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-zinc-200 bg-zinc-50 text-slate-700'
      } ${className}`}
      title={incomplete ? data.feature_notes || 'Eingeschränkter Funktionsumfang.' : data.name}
      data-testid={`country-badge-${data.code.toLowerCase()}`}
    >
      <span aria-hidden>{data.flag}</span>
      <span className="font-mono uppercase tracking-wider">{data.code}</span>
      {incomplete && <Info className="h-3 w-3" />}
    </span>
  );
}

export default CountryFeatureBadge;
